import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { usePersona } from "@/lib/personaStore";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Search, ExternalLink } from "lucide-react";
import StatusPill from "@/components/StatusPill";
import ServiceNowPanel from "@/components/ServiceNowPanel";
import { formatDate, displayReqNumber } from "@/lib/format";

const STEPS = ["Submitted", "Pending Approval", "Approved", "Fulfilled"];

function ProgressStepper({ status, requiresApproval }) {
  const steps = requiresApproval ? STEPS : ["Submitted", "In Progress", "Approved", "Fulfilled"];
  const idx = steps.indexOf(status);
  const rejected = status === "Rejected";
  return (
    <div className="flex items-center gap-1 min-w-[220px]" title={status}>
      {steps.map((s, i) => {
        const done = !rejected && i <= idx;
        const active = !rejected && i === idx;
        return (
          <div key={s} className="flex items-center gap-1 flex-1">
            <div
              className={`h-1.5 flex-1 rounded-full ${
                rejected ? "bg-red-200" : done ? "bg-[#0033A0]" : "bg-slate-200"
              } ${active ? "shadow-[0_0_0_3px_rgba(0,51,160,0.15)]" : ""}`}
            />
          </div>
        );
      })}
    </div>
  );
}

export default function Orders({ embedded = false }) {
  const { persona, userEmail } = usePersona();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [snOpen, setSnOpen] = useState(false);
  const [snTarget, setSnTarget] = useState({ sysId: "", table: "sc_request", number: "", fallbackReq: "" });

  const openSnPanel = (order) => {
    setSnTarget({
      sysId: order.sn_sys_id || "",
      table: order.sn_table || "sc_request",
      number: order.sn_number || "",
      fallbackReq: order.req_number || "",
    });
    setSnOpen(true);
  };

  useEffect(() => {
    setLoading(true);
    endpoints.orders(persona.id, userEmail).then((d) => { setOrders(d); setLoading(false); }).catch(() => setLoading(false));
  }, [persona.id, userEmail]);

  const filtered = orders.filter((o) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return (
      (o.req_number || "").toLowerCase().includes(needle) ||
      (o.sn_number || "").toLowerCase().includes(needle) ||
      (o.device_name || "").toLowerCase().includes(needle)
    );
  });

  return (
    <div className="space-y-6" data-testid="orders-page">
      {!embedded && (
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Orders</div>
          <h1 className="font-display font-black text-3xl text-slate-900 tracking-tight mt-1">Your Device Requests</h1>
          <p className="text-sm text-slate-600 mt-1">Track every ServiceNow REQ generated from Flexspace.</p>
        </div>
      )}

      <Card className="p-4 border-slate-200 shadow-sm">
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="orders-search"
            placeholder="Search by REQ # or device"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 bg-slate-50 border-slate-200"
          />
        </div>
      </Card>

      <Card className="p-0 border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="orders-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">REQ #</th>
              <th className="text-left px-4 py-3 font-semibold">Device</th>
              <th className="text-left px-4 py-3 font-semibold">Submitted</th>
              <th className="text-left px-4 py-3 font-semibold">Delivery</th>
              <th className="text-left px-4 py-3 font-semibold">Progress</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-left px-4 py-3 font-semibold">ServiceNow</th>
            </tr>
          </thead>
          <tbody>
            {loading && Array.from({ length: 4 }).map((_, i) => (
              <tr key={i} className="border-t border-slate-200"><td colSpan={7} className="p-3"><Skeleton className="h-6 w-full" /></td></tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="p-10 text-center text-slate-500">
                No orders found. Place your first order from the <Link to="/catalogue" className="text-[#0033A0] underline">Catalogue</Link>.
              </td></tr>
            )}
            {filtered.map((o) => {
              const displayNum = displayReqNumber(o);
              return (
              <tr key={o.id} className="border-t border-slate-200 hover:bg-slate-50">
                <td className="px-4 py-3">
                  <span data-testid={`order-req-${displayNum}`} className="font-mono text-xs font-semibold text-[#0033A0]">{displayNum}</span>
                  {o.sn_number && o.sn_number !== o.req_number && (
                    <div className="text-[9px] text-slate-400 font-mono mt-0.5" title="Local Flexspace REQ">fx {o.req_number}</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-md overflow-hidden bg-slate-100 shrink-0">
                      <img src={o.device_image} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">{o.device_name}</div>
                      {o.color && <div className="text-[11px] text-slate-500">{o.color}</div>}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600">{formatDate(o.created_at)}</td>
                <td className="px-4 py-3 text-slate-600">{o.delivery_location}</td>
                <td className="px-4 py-3"><ProgressStepper status={o.status} requiresApproval={o.requires_approval} /></td>
                <td className="px-4 py-3"><StatusPill status={o.status} testId={`order-status-${displayNum}`} /></td>
                <td className="px-4 py-3">
                  <button
                    data-testid={`open-sn-${displayNum}`}
                    onClick={() => openSnPanel(o)}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-mono border transition-colors ${
                      o.sn_sys_id
                        ? "bg-[#293e40] text-white border-[#293e40] hover:bg-[#1a2828]"
                        : "bg-slate-50 text-slate-600 border-slate-300 hover:bg-slate-100"
                    }`}
                    title={o.sn_sys_id ? "View live ServiceNow record" : "Preview — connect a PDI to see live record"}
                  >
                    {o.sn_number || (o.sn_sys_id ? "Open" : "Preview")}
                    <ExternalLink size={10} />
                  </button>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </Card>

      <ServiceNowPanel
        open={snOpen}
        onOpenChange={setSnOpen}
        table={snTarget.table}
        sysId={snTarget.sysId}
        number={snTarget.number}
        fallbackReq={snTarget.fallbackReq}
      />
    </div>
  );
}
